import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Switch,
  FormControlLabel,
  Snackbar,
  List,
  ListItem,
  Divider,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Webhook as WebhookIcon,
  PlayArrow as TestIcon,
  History as HistoryIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { webhooksApi } from '../services/api';
import type { Webhook, CreateWebhookDto, UpdateWebhookDto, WebhookLog } from '../types/api';

const WebhookVerwaltung: React.FC = () => {
  const [webhooks, setWebhooks] = useState<Webhook[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [logsDialogOpen, setLogsDialogOpen] = useState(false);
  const [editingWebhook, setEditingWebhook] = useState<Webhook | null>(null);
  const [selectedWebhookForLogs, setSelectedWebhookForLogs] = useState<Webhook | null>(null);
  const [webhookLogs, setWebhookLogs] = useState<WebhookLog[]>([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [testingWebhookId, setTestingWebhookId] = useState<number | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info',
  });

  const [formData, setFormData] = useState<CreateWebhookDto>({
    name: '',
    beschreibung: '',
    webhookUrl: '',
    methode: 'POST',
    headers: '',
    aktiv: true,
    triggerType: 'Manual',
  });

  useEffect(() => {
    loadWebhooks();
  }, []);

  const loadWebhooks = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await webhooksApi.getAll();
      setWebhooks(response.data);
    } catch (err: any) {
      console.error('Webhook-Fehler:', err);
      setError(err.response?.data?.message || 'Fehler beim Laden der Webhooks');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (webhook?: Webhook) => {
    if (webhook) {
      setEditingWebhook(webhook);
      setFormData({
        name: webhook.name,
        beschreibung: webhook.beschreibung || '',
        webhookUrl: webhook.webhookUrl,
        methode: webhook.methode,
        headers: webhook.headers || '',
        aktiv: webhook.aktiv,
        triggerType: webhook.triggerType || 'Manual',
      });
    } else {
      setEditingWebhook(null);
      setFormData({
        name: '',
        beschreibung: '',
        webhookUrl: '',
        methode: 'POST',
        headers: '',
        aktiv: true,
        triggerType: 'Manual',
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingWebhook(null);
  };

  const handleSave = async () => {
    try {
      if (editingWebhook) {
        await webhooksApi.update(editingWebhook.webhookID, formData as UpdateWebhookDto);
      } else {
        await webhooksApi.create(formData);
      }
      await loadWebhooks();
      handleCloseDialog();
      setSnackbar({
        open: true,
        message: 'Webhook erfolgreich gespeichert',
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Fehler beim Speichern:', err);
      setSnackbar({
        open: true,
        message: 'Fehler beim Speichern: ' + (err.response?.data?.message || err.message),
        severity: 'error',
      });
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Webhook wirklich löschen?')) return;
    try {
      await webhooksApi.delete(id);
      await loadWebhooks();
      setSnackbar({
        open: true,
        message: 'Webhook erfolgreich gelöscht',
        severity: 'success',
      });
    } catch (err: any) {
      console.error('Fehler beim Löschen:', err);
      setSnackbar({
        open: true,
        message: 'Fehler beim Löschen: ' + (err.response?.data?.message || err.message),
        severity: 'error',
      });
    }
  };

  const handleTestWebhook = async (webhookId: number, webhookName: string) => {
    setTestingWebhookId(webhookId);
    try {
      const response = await webhooksApi.test(webhookId);
      const result = response.data;

      if (result.success) {
        setSnackbar({
          open: true,
          message: `✓ Webhook "${webhookName}" erfolgreich getestet! (${result.durationMs}ms, Status: ${result.statusCode})`,
          severity: 'success',
        });
      } else {
        setSnackbar({
          open: true,
          message: `✗ Webhook-Test fehlgeschlagen: ${result.errorMessage}`,
          severity: 'error',
        });
      }
    } catch (err: any) {
      console.error('Fehler beim Testen:', err);
      setSnackbar({
        open: true,
        message: 'Fehler beim Testen: ' + (err.response?.data?.message || err.message),
        severity: 'error',
      });
    } finally {
      setTestingWebhookId(null);
    }
  };

  const handleOpenLogs = async (webhook: Webhook) => {
    setSelectedWebhookForLogs(webhook);
    setLogsDialogOpen(true);
    setLoadingLogs(true);

    try {
      const response = await webhooksApi.getLogs(webhook.webhookID, 50);
      setWebhookLogs(response.data);
    } catch (err: any) {
      console.error('Fehler beim Laden der Logs:', err);
      setSnackbar({
        open: true,
        message: 'Fehler beim Laden der Logs: ' + (err.response?.data?.message || err.message),
        severity: 'error',
      });
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleCloseLogs = () => {
    setLogsDialogOpen(false);
    setSelectedWebhookForLogs(null);
    setWebhookLogs([]);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <WebhookIcon sx={{ fontSize: 32, color: 'primary.main' }} />
          <Typography variant="h4">Webhook-Verwaltung (N8N)</Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDialog()}
          >
            Neuer Webhook
          </Button>
          <Tooltip title="Aktualisieren">
            <IconButton onClick={loadWebhooks} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Beschreibung</TableCell>
              <TableCell>URL</TableCell>
              <TableCell>Methode</TableCell>
              <TableCell>Trigger-Typ</TableCell>
              <TableCell>Erstellt am</TableCell>
              <TableCell align="right">Aktionen</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {webhooks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography color="textSecondary">
                    Keine Webhooks vorhanden
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              webhooks.map((webhook) => (
                <TableRow key={webhook.webhookID} hover>
                  <TableCell>
                    <Chip
                      label={webhook.aktiv ? 'Aktiv' : 'Inaktiv'}
                      color={webhook.aktiv ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {webhook.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="textSecondary">
                      {webhook.beschreibung || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {webhook.webhookUrl}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={webhook.methode}
                      color={webhook.methode === 'POST' ? 'primary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={webhook.triggerType || 'Manual'}
                      color={
                        webhook.triggerType === 'Registration' ? 'secondary' :
                        webhook.triggerType === 'TicketCreated' ? 'info' :
                        webhook.triggerType === 'TicketCompleted' ? 'success' :
                        'default'
                      }
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {new Date(webhook.erstelltAm).toLocaleDateString('de-DE')}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Webhook testen">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleTestWebhook(webhook.webhookID, webhook.name)}
                          color="success"
                          disabled={testingWebhookId === webhook.webhookID}
                        >
                          {testingWebhookId === webhook.webhookID ? (
                            <CircularProgress size={20} />
                          ) : (
                            <TestIcon />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Logs anzeigen">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenLogs(webhook)}
                        color="info"
                      >
                        <HistoryIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Bearbeiten">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(webhook)}
                        color="primary"
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Löschen">
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(webhook.webhookID)}
                        color="error"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog für Erstellen/Bearbeiten */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingWebhook ? 'Webhook bearbeiten' : 'Neuer Webhook'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Name"
              fullWidth
              required
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              helperText="Sprechender Name für den Webhook (z.B. 'Ticket-Benachrichtigung')"
            />

            <TextField
              label="Beschreibung"
              fullWidth
              multiline
              rows={2}
              value={formData.beschreibung}
              onChange={(e) => setFormData({ ...formData, beschreibung: e.target.value })}
              helperText="Optionale Beschreibung für den Webhook"
            />

            <TextField
              label="Webhook-URL"
              fullWidth
              required
              value={formData.webhookUrl}
              onChange={(e) => setFormData({ ...formData, webhookUrl: e.target.value })}
              placeholder="https://your-n8n-instance.com/webhook/..."
              helperText="Die vollständige URL des N8N Webhooks"
            />

            <FormControl fullWidth>
              <InputLabel>HTTP-Methode</InputLabel>
              <Select
                value={formData.methode}
                label="HTTP-Methode"
                onChange={(e) => setFormData({ ...formData, methode: e.target.value })}
              >
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
                <MenuItem value="PUT">PUT</MenuItem>
                <MenuItem value="PATCH">PATCH</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Trigger-Typ</InputLabel>
              <Select
                value={formData.triggerType}
                label="Trigger-Typ"
                onChange={(e) => setFormData({ ...formData, triggerType: e.target.value })}
              >
                <MenuItem value="Manual">Manual - Manuell über API auslösen</MenuItem>
                <MenuItem value="Registration">Registration - Bei neuer Benutzer-Registrierung</MenuItem>
                <MenuItem value="TicketCreated">TicketCreated - Bei neuem Ticket</MenuItem>
                <MenuItem value="TicketCompleted">TicketCompleted - Bei abgeschlossenem Ticket</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="HTTP-Headers (JSON)"
              fullWidth
              multiline
              rows={4}
              value={formData.headers}
              onChange={(e) => setFormData({ ...formData, headers: e.target.value })}
              placeholder={'{\n  "Authorization": "Bearer token",\n  "Content-Type": "application/json"\n}'}
              helperText="Optionale HTTP-Headers im JSON-Format"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.aktiv}
                  onChange={(e) => setFormData({ ...formData, aktiv: e.target.checked })}
                />
              }
              label="Webhook aktiv"
            />

            {/* Secret & Callback-URL (nur bei existierenden Webhooks) */}
            {editingWebhook && editingWebhook.secret && (
              <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                  Bidirektionale Kommunikation (N8N → Skeena)
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                    Callback-URL (für N8N HTTP-Request-Node):
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={`${window.location.protocol}//${window.location.host}/api/webhooks/callback/{ticketId}`}
                    InputProps={{
                      readOnly: true,
                      sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
                    }}
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                </Box>

                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
                    Webhook-Secret (im Header "X-Webhook-Secret" mitschicken):
                  </Typography>
                  <TextField
                    fullWidth
                    size="small"
                    value={editingWebhook.secret}
                    InputProps={{
                      readOnly: true,
                      sx: { fontFamily: 'monospace', fontSize: '0.875rem' }
                    }}
                    onClick={(e) => (e.target as HTMLInputElement).select()}
                  />
                </Box>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <strong>N8N-Setup:</strong> Verwenden Sie einen HTTP-Request-Node mit Method=POST,
                  fügen Sie den Header <code>X-Webhook-Secret</code> mit dem obigen Secret hinzu.
                  Im Body können Sie: <code>status</code>, <code>add_comment</code>, oder <code>update_priority</code> senden.
                </Alert>
              </Paper>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={!formData.name || !formData.webhookUrl}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog für Webhook-Logs */}
      <Dialog open={logsDialogOpen} onClose={handleCloseLogs} maxWidth="lg" fullWidth>
        <DialogTitle>
          Webhook-Logs: {selectedWebhookForLogs?.name}
        </DialogTitle>
        <DialogContent>
          {loadingLogs ? (
            <Box display="flex" justifyContent="center" p={4}>
              <CircularProgress />
            </Box>
          ) : webhookLogs.length === 0 ? (
            <Box p={4} textAlign="center">
              <Typography color="textSecondary">
                Keine Logs vorhanden
              </Typography>
            </Box>
          ) : (
            <List>
              {webhookLogs.map((log, index) => (
                <React.Fragment key={log.logID}>
                  {index > 0 && <Divider />}
                  <ListItem alignItems="flex-start">
                    <Box sx={{ width: '100%' }}>
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        {log.success ? (
                          <SuccessIcon color="success" fontSize="small" />
                        ) : (
                          <ErrorIcon color="error" fontSize="small" />
                        )}
                        <Chip
                          label={log.triggerType}
                          size="small"
                          color={log.triggerType === 'Test' ? 'info' : 'default'}
                        />
                        <Typography variant="caption" color="textSecondary">
                          {new Date(log.executedAt).toLocaleString('de-DE')}
                        </Typography>
                        {log.durationMs && (
                          <Typography variant="caption" color="textSecondary">
                            ({log.durationMs}ms)
                          </Typography>
                        )}
                        {log.responseStatusCode && (
                          <Chip
                            label={`HTTP ${log.responseStatusCode}`}
                            size="small"
                            color={log.responseStatusCode >= 200 && log.responseStatusCode < 300 ? 'success' : 'error'}
                          />
                        )}
                      </Box>

                      {log.ticketID && (
                        <Typography variant="body2" gutterBottom>
                          <strong>Ticket-ID:</strong> {log.ticketID}
                        </Typography>
                      )}

                      <Typography variant="body2" gutterBottom>
                        <strong>Request:</strong> {log.requestMethod} {log.requestUrl}
                      </Typography>

                      {log.requestBody && (
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            Request Body:
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 1, mt: 0.5, mb: 1, maxHeight: 150, overflow: 'auto' }}>
                            <Typography
                              variant="body2"
                              component="pre"
                              sx={{ fontFamily: 'monospace', fontSize: '0.75rem', m: 0, whiteSpace: 'pre-wrap' }}
                            >
                              {log.requestBody}
                            </Typography>
                          </Paper>
                        </Box>
                      )}

                      {log.responseBody && (
                        <Box>
                          <Typography variant="caption" color="textSecondary">
                            Response:
                          </Typography>
                          <Paper variant="outlined" sx={{ p: 1, mt: 0.5, mb: 1, maxHeight: 150, overflow: 'auto' }}>
                            <Typography
                              variant="body2"
                              component="pre"
                              sx={{ fontFamily: 'monospace', fontSize: '0.75rem', m: 0, whiteSpace: 'pre-wrap' }}
                            >
                              {log.responseBody}
                            </Typography>
                          </Paper>
                        </Box>
                      )}

                      {log.errorMessage && (
                        <Alert severity="error" sx={{ mt: 1 }}>
                          {log.errorMessage}
                        </Alert>
                      )}
                    </Box>
                  </ListItem>
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseLogs}>Schließen</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar für Feedback */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default WebhookVerwaltung;
